/**
 * FooterActionBar — page-bottom CTA row for forms / wizards.
 *
 * Observed pattern: the primary submit (`good` tone, e.g. "Сохранить
 * медиаплан") next to a secondary skip / cancel ("На страницу проекта").
 * Always at the bottom of the form page, sometimes centered, sometimes
 * left-aligned.
 *
 * Anatomy:
 *
 *   ┌────────────────────────────────────────────────────┐
 *   │ [primary]  [secondary]                              │
 *   └────────────────────────────────────────────────────┘
 *
 * Caller supplies the button nodes themselves so brand colour / icon /
 * loading state are all delegated to the underlying `<Button>` atom.
 *
 * @format
 */

import {forwardRef, type ReactNode} from 'react';

import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';

export const FOOTER_ACTION_BAR_ALIGN = ['start', 'center', 'end'] as const;
export type FooterActionBarAlign = (typeof FOOTER_ACTION_BAR_ALIGN)[number];

export interface FooterActionBarProps {
  /** Primary CTA (Button). Usually `color="good"`. */
  primary: ReactNode;
  /** Optional secondary action (skip, cancel, back). */
  secondary?: ReactNode;
  /** Horizontal alignment of the bar's contents. Default `'start'`. */
  align?: FooterActionBarAlign;
  className?: string;
}

const Root = styled('footer', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$align: FooterActionBarAlign}>(({$align}) => ({
  display: 'flex',
  justifyContent: $align === 'center' ? 'center' : $align === 'end' ? 'flex-end' : 'flex-start',
  width: '100%',
}));

export const FooterActionBar = forwardRef<HTMLElement, FooterActionBarProps>(
  function FooterActionBar({primary, secondary, align = 'start', className}, ref) {
    return (
      <Root ref={ref} $align={align} className={className}>
        <Row gap='s' alignY='center' reflowAt='sm'>
          {primary}
          {secondary}
        </Row>
      </Root>
    );
  },
);
